import React, { useState, useEffect } from 'react';
import { Plus, Search, Trash2, TrendingUp, TrendingDown, DollarSign, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { useTransactions } from '../hooks/useTransactions';
import { useInvestmentContext } from '../context/InvestmentContext';
import { cashMovementsAPI } from '../api/client';
import TransactionForm from '../components/TransactionForm';
import { formatCurrency, formatDate } from '../utils/formatters';

const TX_TYPE_STYLES = {
  buy: 'bg-green-100 text-green-700',
  sell: 'bg-red-100 text-red-700',
  dividend: 'bg-blue-100 text-blue-700',
  transfer: 'bg-purple-100 text-purple-700',
  fee: 'bg-gray-100 text-gray-700',
  deposit: 'bg-emerald-100 text-emerald-700',
  withdrawal: 'bg-orange-100 text-orange-700',
};

const TX_TYPE_ICONS = {
  buy: <TrendingUp className="w-4 h-4" />,
  sell: <TrendingDown className="w-4 h-4" />,
  dividend: <DollarSign className="w-4 h-4" />,
  deposit: <ArrowDownCircle className="w-4 h-4" />,
  withdrawal: <ArrowUpCircle className="w-4 h-4" />,
};

const CASH_MOVEMENT_TYPES = new Set(['deposit', 'withdrawal']);

const TransactionsScreen = () => {
  const { goals, brokers, assets, fetchPortfolio, fetchGoals } = useInvestmentContext();
  const {
    transactions,
    loading,
    error,
    fetchTransactions,
    createTransaction,
    deleteTransaction,
    clearError,
  } = useTransactions();

  const [cashMovements, setCashMovements] = useState([]);
  const [cmLoading, setCmLoading] = useState(false);
  const [confirmDeleteCm, setConfirmDeleteCm] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    const isCashFilter = CASH_MOVEMENT_TYPES.has(typeFilter);
    if (!isCashFilter) {
      fetchTransactions({ search: searchQuery || undefined, transaction_type: typeFilter !== 'all' ? typeFilter : undefined });
    }
  }, [searchQuery, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCmLoading(true);
    cashMovementsAPI.list()
      .then((res) => setCashMovements(res.data.results ?? res.data))
      .catch(() => {})
      .finally(() => setCmLoading(false));
  }, []);

  const handleDeleteCm = async (id) => {
    await cashMovementsAPI.delete(id);
    setCashMovements((prev) => prev.filter((m) => m.id !== id));
    setConfirmDeleteCm(null);
  };

  // Build unified sorted list
  const isCashFilter = CASH_MOVEMENT_TYPES.has(typeFilter);
  const showTransactions = typeFilter === 'all' || !isCashFilter;
  const showCashMovements = typeFilter === 'all' || isCashFilter;

  const filteredCm = showCashMovements
    ? cashMovements.filter((m) => typeFilter === 'all' || m.movement_type === typeFilter)
    : [];

  const txItems = showTransactions ? transactions.map((t) => ({ ...t, _kind: 'tx' })) : [];
  const cmItems = filteredCm.map((m) => ({ ...m, _kind: 'cm' }));

  const unifiedList = [...txItems, ...cmItems].sort((a, b) => {
    const dateA = a._kind === 'tx' ? a.transaction_date : a.date;
    const dateB = b._kind === 'tx' ? b.transaction_date : b.date;
    return dateB.localeCompare(dateA);
  });

  const handleCreate = async (data) => {
    await createTransaction(data);
    setShowForm(false);
    fetchPortfolio();
    fetchGoals();
  };

  const handleDelete = async (id) => {
    await deleteTransaction(id);
    setConfirmDelete(null);
    fetchPortfolio();
    fetchGoals();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
            <p className="text-gray-500 text-sm">{unifiedList.length} total</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New
          </button>
        </div>

        {/* Search */}
        <div className="max-w-4xl mx-auto relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by symbol or notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Type filter */}
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto pb-1">
          {['all', 'buy', 'sell', 'dividend', 'transfer', 'fee', 'deposit', 'withdrawal'].map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition ${
                typeFilter === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex justify-between mb-4">
            <span>{error}</span>
            <button onClick={clearError} className="font-medium">Dismiss</button>
          </div>
        )}

        {(loading || cmLoading) ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : unifiedList.length === 0 ? (
          <div className="text-center py-16">
            <TrendingUp className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No transactions found</p>
            <p className="text-gray-400 text-sm mt-1">Start recording your investments</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
            >
              Add Transaction
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {unifiedList.map((item) => {
              if (item._kind === 'cm') {
                const isDeposit = item.movement_type === 'deposit';
                const brokerName = brokers.find((b) => b.id === item.broker)?.name ?? `Broker #${item.broker}`;
                return (
                  <div
                    key={`cm-${item.id}`}
                    className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition"
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${
                        isDeposit ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                      }`}
                    >
                      {isDeposit ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900">{brokerName}</p>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isDeposit ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                          }`}
                        >
                          {isDeposit ? 'Deposit' : 'Withdrawal'}
                        </span>
                      </div>
                      {item.notes && <p className="text-xs text-gray-500 truncate">{item.notes}</p>}
                      <p className="text-xs text-gray-400">{formatDate(item.date)}</p>
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold ${isDeposit ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {isDeposit ? '+' : '-'}{formatCurrency(parseFloat(item.amount))}
                      </p>
                      <button
                        onClick={() => setConfirmDeleteCm(item.id)}
                        className="mt-1 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                        title="Delete movement"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              }

              // Regular transaction
              const tx = item;
              return (
                <div
                  key={`tx-${tx.id}`}
                  className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition"
                >
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${
                      TX_TYPE_STYLES[tx.transaction_type] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {TX_TYPE_ICONS[tx.transaction_type] || <DollarSign className="w-4 h-4" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-gray-900">{tx.asset_symbol}</p>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          TX_TYPE_STYLES[tx.transaction_type]
                        }`}
                      >
                        {tx.transaction_type_display || tx.transaction_type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">{tx.asset_name}</p>
                    <p className="text-xs text-gray-400">
                      {tx.quantity} units @ {formatCurrency(parseFloat(tx.price_per_unit))} &middot; {tx.broker_name}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(tx.transaction_date)}</p>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-gray-900">{formatCurrency(parseFloat(tx.total_cost))}</p>
                    <p className="text-xs text-gray-400">{formatCurrency(parseFloat(tx.total_amount))} inv.</p>
                    <button
                      onClick={() => setConfirmDelete(tx.id)}
                      className="mt-1 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Delete transaction"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <TransactionForm
          goals={goals}
          brokers={brokers}
          assets={assets}
          onSubmit={handleCreate}
          onClose={() => setShowForm(false)}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Transaction?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently remove this transaction and update your portfolio. Cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteCm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Movement?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently remove this cash movement. Cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteCm(null)}
                className="flex-1 py-2 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCm(confirmDeleteCm)}
                className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsScreen;
